// Developer Platform — Tab: Applications (auth-service /developer/applications).
// OAuth authorization_code + PKCE apps that act on behalf of a signed-in USER — distinct from
// the client-credentials API clients. Register an app (redirect URIs + scopes + public/confidential),
// see the one-time secret for confidential apps, and disable. The end-user authorization happens on
// the consent screen (OAuthConsentPage), reached when a third party sends a user to /oauth/consent.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage, isEntitlementUnavailable } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { AppWindow, BadgeCheck, Loader2, Plus, Power, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { TruncatedText } from '@/features/shared/components/ui/TruncatedText';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  createApplication, disableApplication, getScopeCatalog, listApplications,
  listWebhookSubscriptions, requestCertification, runCertificationChecks,
  isNotEntitled, isNotRolledOut, type CertificationEvidence,
  type DeveloperApplication, type ScopeCategory, type WebhookSubscription,
} from '@/services/developerService';
import { CopyButton, FeatureNotEnabledState, RolloutPendingState, SecretModal, errMsg, fmtDateTime, inputCls, TemporarilyUnavailableState } from './common';

const FALLBACK_SCOPES: ScopeCategory[] = [
  { category: 'employees', scopes: [{ scope: 'employees:read', description: 'Read the employee directory', sensitive: false }] },
];

// ------------------------------------------------------- create dialog (wizard)
const CreateAppDialog: React.FC<{
  catalog: ScopeCategory[];
  onCreated: (app: DeveloperApplication, secret?: string) => void;
  onClose: () => void;
}> = ({ catalog, onCreated, onClose }) => {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [environment, setEnvironment] = useState('live');
  const [confidential, setConfidential] = useState(false);
  const [kind, setKind] = useState<'app' | 'agent'>('app');
  const [redirectText, setRedirectText] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [sensitiveAck, setSensitiveAck] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggle = (scope: string) =>
    setSelected((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));

  const redirectUris = useMemo(
    () => redirectText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    [redirectText],
  );

  // M2: which selected scopes are sensitive (from the catalog). Granting any requires an explicit ack.
  const sensitiveSet = useMemo(() => {
    const s = new Set<string>();
    catalog.forEach((c) => c.scopes.forEach((x) => x.sensitive && s.add(x.scope)));
    return s;
  }, [catalog]);
  const selectedSensitive = selected.filter((s) => sensitiveSet.has(s));

  const submit = async () => {
    if (!name.trim()) return toast.error(t('developer.app_name_required', { defaultValue: 'Give the application a name.' }));
    if (redirectUris.length === 0) return toast.error(t('developer.redirects_required', { defaultValue: 'Add at least one redirect URI.' }));
    if (selected.length === 0) return toast.error(t('developer.scopes_required', { defaultValue: 'Select at least one scope.' }));
    if (selectedSensitive.length > 0 && !sensitiveAck) {
      return toast.error(t('developer.sensitive_ack_required', { defaultValue: 'Acknowledge the sensitive scopes before continuing.' }));
    }
    setSaving(true);
    try {
      const { application, clientSecret } = await createApplication({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        redirect_uris: redirectUris,
        scopes: selected,
        kind,
        environment,
        confidential,
        acknowledgeSensitive: selectedSensitive.length > 0 ? sensitiveAck : undefined,
      });
      onCreated(application, clientSecret);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.app_create_failed', { defaultValue: 'Could not create the application.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('developer.new_app', { defaultValue: 'New application' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="dev-app-name">
                {t('developer.app_name', { defaultValue: 'Name' })}
              </label>
              <input id="dev-app-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t('developer.app_name_ph', { defaultValue: 'e.g. Expense partner app' })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="dev-app-env">
                {t('developer.environment', { defaultValue: 'Environment' })}
              </label>
              <select id="dev-app-env" className={inputCls} value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                <option value="live">{t('developer.env_live', { defaultValue: 'Live' })}</option>
                <option value="sandbox">{t('developer.env_sandbox', { defaultValue: 'Sandbox' })}</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="dev-app-desc">
              {t('developer.app_desc', { defaultValue: 'Description (optional)' })}
            </label>
            <input id="dev-app-desc" className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder={t('developer.app_desc_ph', { defaultValue: 'What does this application do?' })} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="dev-app-redirects">
              {t('developer.redirect_uris', { defaultValue: 'Redirect URIs (one per line)' })}
            </label>
            <textarea id="dev-app-redirects" className={`${inputCls} min-h-[72px] font-mono`} value={redirectText}
              onChange={(e) => setRedirectText(e.target.value)}
              placeholder={'https://app.example.com/oauth/callback'} />
            <p className="text-[11px] text-muted-foreground">
              {t('developer.redirect_help', { defaultValue: 'Must be https (http allowed only for localhost). Codes are only returned to an exact match.' })}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t('developer.app_kind', { defaultValue: 'What is this?' })}</p>
            <div className="grid grid-cols-2 gap-2">
              {([
                { value: 'app' as const, label: t('developer.kind_app', { defaultValue: 'Application' }), help: t('developer.kind_app_help', { defaultValue: 'A web/server integration your team operates.' }) },
                { value: 'agent' as const, label: t('developer.kind_agent', { defaultValue: 'AI agent (MCP)' }), help: t('developer.kind_agent_help', { defaultValue: 'An AI assistant acting for a consenting user — audited as agent traffic.' }) },
              ]).map((opt) => (
                <button key={opt.value} type="button" onClick={() => setKind(opt.value)}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${kind === opt.value ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.08]' : 'border-border hover:bg-muted/40'}`}>
                  <span className="block text-sm font-medium text-foreground">{opt.label}</span>
                  <span className="block text-xs text-muted-foreground">{opt.help}</span>
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/40">
            <input type="checkbox" className="w-4 h-4 mt-0.5 accent-orange-500 shrink-0" checked={confidential}
              onChange={(e) => setConfidential(e.target.checked)} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">
                {t('developer.confidential', { defaultValue: 'Confidential client (has a secret)' })}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t('developer.confidential_help', { defaultValue: 'Enable only for server-side apps that can keep a secret. Native/single-page apps should stay public (PKCE only).' })}
              </span>
            </span>
          </label>

          <div>
            <p className="text-xs text-muted-foreground mb-1.5">{t('developer.scopes', { defaultValue: 'Scopes' })}</p>
            <div className="space-y-3">
              {catalog.map((cat) => (
                <div key={cat.category}>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{cat.category}</p>
                  <div className="space-y-1.5">
                    {cat.scopes.map((s) => (
                      <label key={s.scope} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/40">
                        <input type="checkbox" className="w-4 h-4 mt-0.5 accent-orange-500 shrink-0"
                          checked={selected.includes(s.scope)} onChange={() => toggle(s.scope)} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-foreground font-mono">{s.scope}</span>
                            {s.sensitive && (
                              <span className="text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-warning-soft text-warning-strong border border-warning">
                                {t('developer.sensitive', { defaultValue: 'sensitive' })}
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-muted-foreground">{s.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedSensitive.length > 0 && (
            <label className="flex items-start gap-2.5 rounded-lg px-3 py-2 text-sm border bg-warning-soft border-warning text-warning-strong cursor-pointer">
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-amber-600 shrink-0" checked={sensitiveAck}
                onChange={(e) => setSensitiveAck(e.target.checked)} />
              <span>
                {t('developer.sensitive_ack', {
                  defaultValue: 'I understand these scopes grant sensitive access ({{scopes}}) to whoever holds this application’s credentials.',
                  scopes: selectedSensitive.join(', '),
                })}
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('developer.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('developer.create_app', { defaultValue: 'Create application' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------------------------------------------------- tab
export const ApplicationsTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();

  const [apps, setApps] = useState<DeveloperApplication[]>([]);
  const [catalog, setCatalog] = useState<ScopeCategory[]>(FALLBACK_SCOPES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [notEnabledMsg, setNotEnabledMsg] = useState<string | null>(null);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [certifying, setCertifying] = useState<DeveloperApplication | null>(null);
  const [secret, setSecret] = useState<{ title: string; description: string; value: string } | null>(null);

  const load = () => {
    listApplications().then(setApps).catch(() => undefined);
  };

  useEffect(() => {
    let alive = true;
    Promise.all([listApplications(), getScopeCatalog().catch(() => FALLBACK_SCOPES)])
      .then(([list, cat]) => {
        if (!alive) return;
        setApps(list);
        if (cat.length) setCatalog(cat);
      })
      .catch((e) => {
        if (!alive) return;
        if (isEntitlementUnavailable(e)) setUnavailableMsg(errMsg(e, ''));
        else if (isNotEntitled(e)) setNotEnabledMsg(errMsg(e, ''));
        else if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const onCreated = (app: DeveloperApplication, clientSecret?: string) => {
    setApps((cur) => [app, ...cur]);
    setCreating(false);
    if (clientSecret) {
      setSecret({
        title: t('developer.app_secret_title', { defaultValue: 'Client secret — {{name}}', name: app.name }),
        description: t('developer.app_secret_desc', {
          defaultValue: 'Confidential clients present this secret alongside the PKCE verifier at token exchange. Stored hashed — it is shown only once.',
        }),
        value: clientSecret,
      });
    } else {
      toast.success(t('developer.app_created_public', { defaultValue: 'Application created (public client — PKCE only, no secret).' }));
    }
  };

  const onDisable = async (app: DeveloperApplication) => {
    const ok = await confirm({
      title: t('developer.app_disable_title', { defaultValue: 'Disable application' }),
      message: t('developer.app_disable_confirm', {
        defaultValue: 'Disable “{{name}}”? New authorizations and token exchanges are rejected immediately.', name: app.name,
      }),
      confirmLabel: t('developer.disable', { defaultValue: 'Disable' }),
      danger: true,
    });
    if (!ok) return;
    setBusyId(app.id);
    try {
      await disableApplication(app.id);
      setApps((cur) => cur.map((a) => (a.id === app.id ? { ...a, status: 'disabled' } : a)));
      toast.success(t('developer.app_disabled', { defaultValue: 'Application disabled.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.status_failed', { defaultValue: 'Could not change the status.' }))));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('developer.loading_apps', { defaultValue: 'Loading applications…' })}
      </div>
    );
  }
  if (unavailableMsg !== null) return <TemporarilyUnavailableState message={unavailableMsg} />;
  if (notEnabledMsg !== null) return <FeatureNotEnabledState message={notEnabledMsg} />;
  if (rolloutPending) {
    return <RolloutPendingState message={t('developer.apps_rollout', { defaultValue: 'Applications are being rolled out to your workspace. Check back soon.' })} />;
  }
  if (loadError) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
        {t('developer.apps_load_error', { defaultValue: 'Could not load applications.' })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('developer.apps_subtitle', {
            defaultValue: 'OAuth applications that act on behalf of a signed-in user via authorization code + PKCE. Use these for third-party apps; use API clients for server-to-server access.',
          })}
        </p>
        <Button onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" /> {t('developer.new_app', { defaultValue: 'New application' })}
        </Button>
      </div>

      {apps.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center text-muted-foreground">
            <AppWindow className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">{t('developer.no_apps', { defaultValue: 'No applications yet. Register one to enable the authorization-code flow.' })}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('developer.col_name', { defaultValue: 'Name' })}</TableHead>
                  <TableHead>{t('developer.col_client_id', { defaultValue: 'Client ID' })}</TableHead>
                  <TableHead>{t('developer.col_type', { defaultValue: 'Type' })}</TableHead>
                  <TableHead>{t('developer.col_redirects', { defaultValue: 'Redirect URIs' })}</TableHead>
                  <TableHead>{t('developer.col_scopes', { defaultValue: 'Scopes' })}</TableHead>
                  <TableHead>{t('developer.col_status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead className="text-right">{t('developer.col_actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <p className="text-sm font-medium text-foreground">{a.name}</p>
                      {a.description && <TruncatedText as="p" className="text-xs text-muted-foreground max-w-[220px]">{a.description}</TruncatedText>}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1">
                        <code className="text-xs font-mono text-foreground">{a.clientId}</code>
                        <CopyButton text={a.clientId} label={t('developer.copy_client_id', { defaultValue: 'Copy client ID' })} />
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className="border bg-muted text-muted-foreground border-border inline-flex items-center gap-1">
                        {a.confidential
                          ? <><ShieldCheck className="w-3 h-3" />{t('developer.confidential_short', { defaultValue: 'Confidential' })}</>
                          : t('developer.public_short', { defaultValue: 'Public (PKCE)' })}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5 max-w-[220px]">
                        {(a.redirectUris || []).slice(0, 2).map((u) => (
                          <code key={u} className="text-[11px] font-mono text-muted-foreground truncate">{u}</code>
                        ))}
                        {(a.redirectUris || []).length > 2 && (
                          <span className="text-[11px] text-muted-foreground">+{a.redirectUris.length - 2} {t('developer.more', { defaultValue: 'more' })}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {(a.scopes || []).map((s) => (
                          <span key={s} className="text-[11px] font-mono text-foreground bg-muted border border-border rounded-full px-2 py-0.5">{s}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border ${a.status === 'active'
                        ? 'bg-success-soft text-success-strong border-transparent'
                        : 'bg-danger-soft text-danger-strong border-transparent'}`}>
                        {a.status === 'active'
                          ? t('developer.status_active', { defaultValue: 'Active' })
                          : t('developer.status_disabled', { defaultValue: 'Disabled' })}
                      </Badge>
                      {a.certificationStatus && a.certificationStatus !== 'none' && (
                        <Badge className={`border mt-1 block w-fit ${{
                          certified: 'bg-success-soft text-success-strong border-transparent',
                          in_review: 'bg-warning-soft text-warning-strong border-transparent',
                          rejected: 'bg-danger-soft text-danger-strong border-transparent',
                        }[a.certificationStatus] || 'bg-muted text-muted-foreground border-border'}`}>
                          {a.certificationStatus === 'certified'
                            ? t('developer.certified', { defaultValue: 'Certified' })
                            : a.certificationStatus.replace('_', ' ')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {a.status === 'active' && a.certificationStatus !== 'certified' && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                          onClick={() => setCertifying(a)}
                          aria-label={t('developer.certify', { defaultValue: 'Certification' })}
                          title={t('developer.certify', { defaultValue: 'Certification' })}>
                          <BadgeCheck className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {a.status === 'active' && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-danger hover:bg-red-50 dark:hover:bg-red-500/10"
                          onClick={() => onDisable(a)} disabled={busyId === a.id}
                          aria-label={t('developer.disable', { defaultValue: 'Disable' })} title={t('developer.disable', { defaultValue: 'Disable' })}>
                          <Power className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {creating && <CreateAppDialog catalog={catalog} onCreated={onCreated} onClose={() => setCreating(false)} />}
      {certifying && (
        <CertificationDialog app={certifying} onClose={() => setCertifying(null)} onChanged={load} />
      )}
      {secret && (
        <SecretModal title={secret.title} description={secret.description} secret={secret.value} onClose={() => setSecret(null)} />
      )}
    </div>
  );
};

// --------------------------------------------------- certification dialog (9B)
// Mechanical checks first (webhook signature contract, run by the platform
// harness against one of the tenant's subscriptions), then request an operator
// review. Certified apps carry the badge in this list.
const CertificationDialog: React.FC<{
  app: DeveloperApplication;
  onClose: () => void;
  onChanged: () => void;
}> = ({ app, onClose, onChanged }) => {
  const { t } = useTranslation('admin');
  const [subs, setSubs] = useState<WebhookSubscription[]>([]);
  const [subId, setSubId] = useState('');
  const [evidence, setEvidence] = useState<CertificationEvidence | null>(null);
  const [running, setRunning] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    listWebhookSubscriptions()
      .then((list) => {
        setSubs(list);
        if (list.length === 1) setSubId(list[0].id);
      })
      .catch(() => setSubs([]));
  }, []);

  const run = async () => {
    if (!subId) return;
    setRunning(true);
    try {
      setEvidence(await runCertificationChecks(app.id, subId));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.cert_run_failed', { defaultValue: 'Could not run the checks.' }))));
    } finally {
      setRunning(false);
    }
  };

  const request = async () => {
    setRequesting(true);
    try {
      await requestCertification(app.id);
      toast.success(t('developer.cert_requested', { defaultValue: 'Submitted for review — the platform team will get back to you.' }));
      onChanged();
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.cert_request_failed', { defaultValue: 'Could not request the review.' }))));
    } finally {
      setRequesting(false);
    }
  };

  const checkRows = Object.entries(evidence?.checks?.checks || {});
  const passed = evidence?.passed === true;
  const inputCls = 'w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-1.5';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('developer.cert_title', { defaultValue: 'Certification — {{name}}', name: app.name })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('developer.cert_intro', {
              defaultValue: 'Certification verifies your integration follows the platform contract. The automated step sends two pings to one of your webhook endpoints: a correctly-signed one (must be accepted) and one with an invalid signature (must be rejected).',
            })}
          </p>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t('developer.cert_sub', { defaultValue: 'Webhook subscription to test' })}
            </label>
            <select className={inputCls} value={subId} onChange={(e) => setSubId(e.target.value)}>
              <option value="">{t('developer.cert_pick', { defaultValue: 'Choose a subscription…' })}</option>
              {subs.map((sTop) => (
                <option key={sTop.id} value={sTop.id}>{sTop.url}</option>
              ))}
            </select>
            {subs.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('developer.cert_no_subs', { defaultValue: 'No webhook subscriptions yet — create one on the Webhooks tab first.' })}
              </p>
            )}
          </div>
          {evidence && (
            <div className="rounded-md border border-border bg-card p-3 space-y-1.5">
              {checkRows.map(([k, v]) => (
                <div key={k} className="flex items-start gap-2 text-sm">
                  <Badge className={`border ${v.passed
                    ? 'bg-success-soft text-success-strong border-transparent'
                    : 'bg-danger-soft text-danger-strong border-transparent'}`}>
                    {v.passed ? 'pass' : 'fail'}
                  </Badge>
                  <div>
                    <p className="text-foreground">{k.replace(/_/g, ' ')}{typeof v.statusCode === 'number' ? ` (HTTP ${v.statusCode})` : ''}</p>
                    {!v.passed && <p className="text-xs text-muted-foreground">{v.error || v.detail}</p>}
                  </div>
                </div>
              ))}
              <p className={`text-sm font-medium ${passed ? 'text-success-strong' : 'text-danger-strong'}`}>
                {passed
                  ? t('developer.cert_passing', { defaultValue: 'All checks passing — you can request review.' })
                  : t('developer.cert_failing', { defaultValue: 'Fix the failing checks and run again.' })}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button variant="outline" onClick={run} disabled={!subId || running}>
            {running && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {t('developer.cert_run', { defaultValue: 'Run checks' })}
          </Button>
          <Button onClick={request} disabled={!passed || requesting}>
            {requesting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {t('developer.cert_request', { defaultValue: 'Request review' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

